import { computed, inject } from '@angular/core';
import { StorageKey, StorageService } from '@lib/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

export const MAX_RECENT_SEARCH_QUERIES = 10;

const NO_ACTIVE_OPTION = -1;

/**
 * Stored recent search queries per user id. The history stays on the device, so several users of
 * the same device share one storage entry and must only ever see their own list.
 */
interface IRecentSearchQueriesByUser {
    readonly [userId: string]: readonly string[];
}

export interface IRecentSearchOption {
    readonly query: string;
    readonly optionId: string;
    readonly active: boolean;
}

interface ISearchFieldState {
    /** Current text of the search field. */
    readonly query: string;
    /** Text typed by the user that filters the list; highlighting an entry does not change it. */
    readonly filter: string;
    /** Text of the field before an entry was highlighted, restored when that entry is removed. */
    readonly textBeforeHighlight: string;
    /** True if the "recent searches" list is offered in the current view. */
    readonly recentSearchesEnabled: boolean;
    /** Id of the logged-in user owning the history, null until known. */
    readonly userId: string | null;
    /** Recent queries of the user, most recent first. */
    readonly recentQueries: readonly string[];
    readonly listOpen: boolean;
    /** Index of the keyboard-highlighted entry within the visible entries. */
    readonly activeIndex: number;
}

const initialState: ISearchFieldState = {
    query: '',
    filter: '',
    textBeforeHighlight: '',
    recentSearchesEnabled: false,
    userId: null,
    recentQueries: [],
    listOpen: false,
    activeIndex: NO_ACTIVE_OPTION,
};

function normalize(query: string): string {
    return query.trim().toLowerCase();
}

function prependQuery(queries: readonly string[], query: string): readonly string[] {
    const normalizedQuery: string = normalize(query);
    return [query, ...queries.filter((entry: string) => normalize(entry) !== normalizedQuery)].slice(
        0,
        MAX_RECENT_SEARCH_QUERIES,
    );
}

function parseQueries(value: unknown): readonly string[] {
    const entries: string[] = Array.isArray(value)
        ? value.filter((entry: unknown): entry is string => typeof entry === 'string')
        : [];
    return entries
        .filter(
            (entry: string, index: number) =>
                entries.findIndex((other) => normalize(other) === normalize(entry)) === index,
        )
        .slice(0, MAX_RECENT_SEARCH_QUERIES);
}

function recentSearchOptionId(index: number): string {
    return `search-recent-option-${index}`;
}

/**
 * State of the global search field: current text and the per-user list of recent searches
 * including its keyboard navigation.
 */
export const SearchFieldStore = signalStore(
    withState(initialState),
    withComputed(({ query, filter, recentQueries }) => ({
        hasQuery: computed(() => query().trim().length > 0),
        visibleRecentQueries: computed(() => {
            const normalizedFilter: string = normalize(filter());
            return recentQueries().filter((entry: string) => entry.toLowerCase().includes(normalizedFilter));
        }),
    })),
    withComputed(({ recentSearchesEnabled, listOpen, visibleRecentQueries, activeIndex }) => ({
        recentListVisible: computed(() => recentSearchesEnabled() && listOpen() && visibleRecentQueries().length > 0),
        activeRecentQuery: computed(() => visibleRecentQueries()[activeIndex()] ?? null),
    })),
    withComputed(({ recentListVisible, activeRecentQuery, activeIndex, visibleRecentQueries }) => ({
        recentOptions: computed((): readonly IRecentSearchOption[] =>
            visibleRecentQueries().map((query: string, index: number) => ({
                query,
                optionId: recentSearchOptionId(index),
                active: index === activeIndex(),
            })),
        ),
        activeOptionId: computed(() =>
            recentListVisible() && activeRecentQuery() !== null ? recentSearchOptionId(activeIndex()) : null,
        ),
    })),
    withMethods((store, storage = inject(StorageService)) => {
        async function readAll(): Promise<IRecentSearchQueriesByUser> {
            return (await storage.getItemObject<IRecentSearchQueriesByUser>(StorageKey.SEARCH_RECENT_QUERIES)) ?? {};
        }

        let pendingStorageAccess: Promise<void> = Promise.resolve();

        /** Runs storage accesses one after another, so they cannot overwrite each other within a tab. */
        function enqueue(access: () => Promise<void>): Promise<void> {
            pendingStorageAccess = pendingStorageAccess.then(access, access);
            return pendingStorageAccess;
        }

        async function reload(userId: string): Promise<void> {
            const all: IRecentSearchQueriesByUser = await readAll();
            patchState(store, { recentQueries: parseQueries(all[userId]) });
        }

        /**
         * Applies the change to the freshly read history instead of the one in memory, so entries
         * added in another tab survive and entries removed there do not come back.
         */
        async function persist(
            userId: string,
            change: (queries: readonly string[]) => readonly string[],
        ): Promise<void> {
            const all: IRecentSearchQueriesByUser = await readAll();
            const queries: readonly string[] = change(parseQueries(all[userId]));
            await storage.setItem(StorageKey.SEARCH_RECENT_QUERIES, JSON.stringify({ ...all, [userId]: queries }));
            patchState(store, { recentQueries: queries });
        }

        function updateQueries(change: (queries: readonly string[]) => readonly string[]): Promise<void> {
            patchState(store, { recentQueries: change(store.recentQueries()), activeIndex: NO_ACTIVE_OPTION });
            const userId: string | null = store.userId();
            return userId ? enqueue(() => persist(userId, change)) : Promise.resolve();
        }

        return {
            /**
             * Loads only the history of the given user and enables the list afterwards, so a search
             * during loading cannot overwrite the stored history.
             */
            async enableRecentSearches(userId: string): Promise<void> {
                const all: IRecentSearchQueriesByUser = await readAll();
                patchState(store, { recentSearchesEnabled: true, userId, recentQueries: parseQueries(all[userId]) });
            },

            /** Text typed by the user, the list is filtered by it. */
            setQuery(query: string): void {
                patchState(store, { query: query ?? '', filter: query ?? '', activeIndex: NO_ACTIVE_OPTION });
            },

            /** Text put into the field by the application; the list shows the whole history again. */
            takeQuery(query: string): void {
                patchState(store, { query: query ?? '', filter: '', activeIndex: NO_ACTIVE_OPTION });
            },

            /**
             * Opens the list and reloads the history, so searches made in another tab show up.
             */
            openList(): Promise<void> {
                const wasOpen: boolean = store.listOpen();
                patchState(store, { listOpen: true });
                const userId: string | null = store.userId();
                return !wasOpen && userId ? enqueue(() => reload(userId)) : Promise.resolve();
            },

            closeList(): void {
                patchState(store, { listOpen: false, activeIndex: NO_ACTIVE_OPTION });
            },

            /**
             * Moves the highlighted entry by the given offset, wraps around at both ends and takes the
             * entry as text of the field without filtering the list by it.
             */
            moveActive(offset: number): void {
                const count: number = store.visibleRecentQueries().length;
                if (!store.recentSearchesEnabled() || count === 0) {
                    return;
                }
                const current: number = store.activeIndex();
                const next: number =
                    current === NO_ACTIVE_OPTION ? (offset > 0 ? 0 : count - 1) : (current + offset + count) % count;
                patchState(store, {
                    listOpen: true,
                    activeIndex: next,
                    query: store.visibleRecentQueries()[next],
                    textBeforeHighlight: current === NO_ACTIVE_OPTION ? store.query() : store.textBeforeHighlight(),
                });
            },

            /** Puts the text the field had before the highlighting back, the filter stays as it is. */
            restoreTextBeforeHighlight(): void {
                patchState(store, { query: store.textBeforeHighlight(), activeIndex: NO_ACTIVE_OPTION });
            },

            /**
             * Puts the query on top of the history; an equal entry (case and surrounding blanks
             * ignored) is replaced, the list is capped at {@link MAX_RECENT_SEARCH_QUERIES}.
             */
            addRecentQuery(query: string): Promise<void> {
                const trimmed: string = query.trim();
                if (!store.recentSearchesEnabled() || !trimmed) {
                    return Promise.resolve();
                }
                return updateQueries((queries: readonly string[]) => prependQuery(queries, trimmed));
            },

            removeRecentQuery(query: string): Promise<void> {
                const normalizedQuery: string = normalize(query);
                return updateQueries((queries: readonly string[]) =>
                    queries.filter((entry: string) => normalize(entry) !== normalizedQuery),
                );
            },
        };
    }),
);
